import { formatMoney } from '../../../utils/formatters';
import type { FinanceSummary } from '../../../types/dashboard';

const MONTHS = [
  'Січень',
  'Лютий',
  'Березень',
  'Квітень',
  'Травень',
  'Червень',
  'Липень',
  'Серпень',
  'Вересень',
  'Жовтень',
  'Листопад',
  'Грудень',
];

function monthLabel(s: string): string {
  const [year, month] = s.split('-');
  return `${MONTHS[parseInt(month, 10) - 1]} ${year}`;
}

/** У таблицях одиниця стоїть у хедері колонки — у клітинці лише число. */
function money(value: string): string {
  return formatMoney(value).replace(/\s*грн$/, '');
}

export function FinanceWidget({ finance }: { finance: FinanceSummary }) {
  const m = finance.this_month;
  const owed = parseFloat(m.owed_uah);
  const currentMonth = finance.by_month.at(-1)?.month;
  const monthName = currentMonth ? monthLabel(currentMonth).split(' ')[0].toLowerCase() : 'місяць';
  const rows = [...finance.by_month].reverse();

  const tiles = [
    {
      label: `Закупівлі за ${monthName}`,
      value: formatMoney(m.purchases_uah),
      sub: 'отримано за документами',
      color: 'var(--text)',
      highlight: false,
    },
    {
      label: 'Оплачено',
      value: formatMoney(m.payments_uah),
      sub: parseFloat(m.payments_uah) > 0 ? 'внесено оплат' : 'оплат ще не внесено',
      color: 'var(--text)',
      highlight: false,
    },
    {
      label: 'Борг постачальникам',
      value: formatMoney(m.owed_uah),
      sub: 'за фактично отримане, за весь час',
      color: owed > 0 ? 'var(--warning)' : 'var(--text)',
      highlight: owed > 0,
    },
    {
      label: `Дохід за ${monthName}`,
      value: formatMoney(m.revenue_uah),
      sub: parseFloat(m.revenue_uah) > 0 ? 'відвантажено' : 'відвантажень поки немає',
      color: 'var(--text)',
      highlight: false,
    },
  ];

  return (
    <div className="dash-card">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 14,
        }}
      >
        <span className="dash-section-label">Фінанси</span>
        <span style={{ fontSize: 12, color: 'var(--text-faint)' }}>
          {currentMonth ? `${monthLabel(currentMonth).toLowerCase()} · ` : ''}
          рахується з документів закупівель
        </span>
      </div>

      <div className="dash-fin-grid">
        {tiles.map((t) => (
          <div
            key={t.label}
            className="dash-fin-tile"
            style={
              t.highlight
                ? { background: 'var(--warning-bg)', borderColor: 'var(--warning-border)' }
                : undefined
            }
          >
            <div className="dash-fin-label">{t.label}</div>
            <div className="dash-fin-value" style={{ color: t.color }}>
              {t.value}
            </div>
            <div className="dash-fin-sub">{t.sub}</div>
          </div>
        ))}
      </div>

      <div className="dash-fin-table">
        <div className="dash-fin-head">
          <span>Місяць</span>
          <span className="dash-fin-num">Закупівлі, грн</span>
          <span className="dash-fin-num">Оплати, грн</span>
          <span className="dash-fin-num">Дохід, грн</span>
          <span className="dash-fin-num">Грошовий потік, грн</span>
        </div>
        {rows.map((row) => (
          <div key={row.month} className="dash-fin-row">
            <span style={{ fontWeight: 600 }}>{monthLabel(row.month)}</span>
            <span className="dash-fin-num">{money(row.purchases_uah)}</span>
            <span className="dash-fin-num" style={{ color: 'var(--text-muted)' }}>
              {money(row.payments_uah)}
            </span>
            <span className="dash-fin-num">{money(row.revenue_uah)}</span>
            <span
              className="dash-fin-num"
              style={{
                fontWeight: 600,
                color: parseFloat(row.profit_uah) < 0 ? 'var(--warning)' : 'var(--text-secondary)',
              }}
            >
              {money(row.profit_uah)}
            </span>
          </div>
        ))}
      </div>

      <div className="dash-fin-note">
        Прибуток за собівартістю зʼявиться разом із модулем фінансових звітів (Фаза 6) — поки що
        показаний касовий грошовий потік.
      </div>
    </div>
  );
}
